import { IconCircleCheck } from 'central-icons/IconCircleCheck';
import { IconCircleInfo } from 'central-icons/IconCircleInfo';
import { IconCircleX } from 'central-icons/IconCircleX';
import { IconCrossSmall } from 'central-icons/IconCrossSmall';
import { IconWarningSign } from 'central-icons/IconWarningSign';
import classNames from 'classnames';

import { Spinner } from '../spinner/Spinner';
import styles from './Toast.module.css';
import type { ToastInstance, ToastStatus } from './toast-types';

function StatusGlyph({ status }: { status: ToastStatus }) {
  switch (status) {
    case 'success':
      return <IconCircleCheck size="20px" />;
    case 'error':
      return <IconCircleX size="20px" />;
    case 'warning':
      return <IconWarningSign size="20px" />;
    case 'loading':
      return <Spinner size="sm" />;
    default:
      return <IconCircleInfo size="20px" />;
  }
}

export interface ToastProps {
  toast: ToastInstance;
  onClose: () => void;
  /** Suspend the auto-dismiss timer while the user is interacting. */
  onPauseTimer?: () => void;
  /** Restart the auto-dismiss timer after interaction ends. */
  onResumeTimer?: () => void;
}

export function Toast({
  toast,
  onClose,
  onPauseTimer,
  onResumeTimer,
}: ToastProps) {
  if (toast.render) {
    return (
      // The viewport is pointer-events: none; render-prop toasts skip the
      // .toast wrapper that restores interactivity, so restore it here.
      <div
        className={styles.custom}
        onMouseEnter={onPauseTimer}
        onMouseLeave={onResumeTimer}
      >
        {toast.render({ id: toast.id, onClose })}
      </div>
    );
  }

  const isError = toast.status === 'error';
  const hasTitle = toast.title !== undefined && toast.title !== null;
  const hasDescription =
    toast.description !== undefined && toast.description !== null;

  return (
    <div
      className={classNames(styles.toast, styles[toast.status])}
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      onMouseEnter={onPauseTimer}
      onMouseLeave={onResumeTimer}
    >
      <span className={styles.icon}>
        <StatusGlyph status={toast.status} />
      </span>
      <div className={styles.body}>
        {hasTitle && <div className={styles.title}>{toast.title}</div>}
        {hasDescription && (
          <div className={styles.description}>{toast.description}</div>
        )}
      </div>
      {toast.isClosable && (
        <button
          type="button"
          className={styles.close}
          aria-label="Close notification"
          onClick={onClose}
        >
          <IconCrossSmall size="14px" />
        </button>
      )}
    </div>
  );
}
